import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type JobRunsResult, type ListJobSchedulesResult } from '@whinerp/client';
import { formatCount, formatDateTime } from '@whinerp/contracts/format';
import { Button, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useFormat, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/**
 * Scheduled jobs (F-092): each job's next and last run, status and duration,
 * its schedule (with the next three times it will run, so a cron expression
 * can be checked by reading it), its run history with reasons, and run-now.
 */

type Schedule = ListJobSchedulesResult['schedules'][number];
const INPUT = 'h-input rounded-control border border-control bg-surface px-3 text-body text-ink';

export function JobsPanel(): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const enabled = useEntitlement('F-092');
  const { can } = useSession();
  const allowed = enabled.enabled && can('setup.modules.manage');
  const schedules = useQuery<ListJobSchedulesResult, ApiError>({
    queryKey: ['job-schedules'],
    queryFn: () => api.listJobSchedules({ body: {} }),
    enabled: allowed,
  });
  if (!allowed) return null;
  return (
    <Panel data-region="scheduled-jobs">
      <div className="flex flex-col gap-2">
        <SectionLabel>{t('jobs.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('jobs.help')}</p>
        {schedules.isPending ? (
          <Skeleton className="h-block-sm w-full" />
        ) : schedules.isError ? (
          <p className="text-dense text-danger-text">{errorText(schedules.error)}</p>
        ) : (
          <ul className="flex flex-col">
            {schedules.data.schedules.map((schedule) => (
              <li key={schedule.scheduleId} className="border-t border-t-rule py-2 first:border-t-0">
                <JobRow schedule={schedule} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  );
}

function JobRow({ schedule }: { schedule: Schedule }): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [cron, setCron] = useState(schedule.cron);
  const [on, setOn] = useState(schedule.enabled);
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['job-schedules'] });
    await queryClient.invalidateQueries({ queryKey: ['job-runs', schedule.scheduleId] });
  };
  const runs = useQuery<JobRunsResult, ApiError>({
    queryKey: ['job-runs', schedule.scheduleId],
    queryFn: () => api.jobRuns({ path: { scheduleId: schedule.scheduleId } }),
    enabled: open,
  });
  const save = useMutation({ mutationFn: () => api.updateJobSchedule({ path: { scheduleId: schedule.scheduleId }, body: { cron, enabled: on } }), onSuccess: refresh });
  const runNow = useMutation({ mutationFn: () => api.runJobNow({ path: { scheduleId: schedule.scheduleId }, body: {} }), onSuccess: refresh });
  const refusal = save.error ?? runNow.error;

  return (
    <div data-job={schedule.jobKey} className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-dense text-ink">{schedule.label}</span>
        <code className="rounded-control bg-canvas px-1 text-meta text-ink-600">{schedule.cron}</code>
        {schedule.lastStatus ? (
          <span data-job-status={schedule.lastStatus} className={schedule.lastStatus === 'succeeded' ? 'text-meta text-success-text' : 'text-meta text-danger-text'}>
            {t(`jobs.status.${schedule.lastStatus}`)}
          </span>
        ) : null}
        <span className="text-meta text-ink-meta">
          {schedule.enabled
            ? schedule.nextRunAt
              ? t('jobs.next', { values: { at: formatDateTime(schedule.nextRunAt, format) } })
              : t('jobs.noNext')
            : t('jobs.off')}
        </span>
        <span className="ms-auto flex gap-2">
          <Button intent="secondary" size="small" data-job-run-now disabled={runNow.isPending} onClick={() => runNow.mutate()}>
            {t('jobs.runNow')}
          </Button>
          <Button intent="ghost" size="small" data-job-open onClick={() => setOpen((value) => !value)}>
            {open ? t('jobs.hide') : t('jobs.details')}
          </Button>
        </span>
      </div>
      {schedule.lastRunAt ? (
        <p data-job-last className="text-meta text-ink-meta">
          {t('jobs.last', {
            values: {
              at: formatDateTime(schedule.lastRunAt, format),
              ms: formatCount(schedule.lastDurationMs ?? 0, format),
              detail: schedule.lastDetail ?? '',
            },
          })}
        </p>
      ) : null}
      {runNow.data ? <p data-job-ran className="text-dense text-ink">{runNow.data.detail}</p> : null}
      {refusal ? <p data-job-refusal className="text-dense text-danger-text">{errorText(refusal)}</p> : null}
      {open ? (
        <div className="flex flex-col gap-2 rounded-card bg-canvas p-2">
          <p className="text-meta text-ink-meta">{schedule.description}</p>
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-dense text-ink-600">
              {t('jobs.schedule')}
              <input data-job-cron className={INPUT} value={cron} onChange={(e) => setCron(e.target.value)} />
            </label>
            <label className="flex items-center gap-2 text-dense text-ink">
              <input type="checkbox" data-job-enabled checked={on} onChange={(e) => setOn(e.target.checked)} />
              {t('jobs.enabled')}
            </label>
            <Button intent="secondary" size="small" data-job-save disabled={save.isPending} onClick={() => save.mutate()}>
              {t('jobs.save')}
            </Button>
          </div>
          <p className="text-meta text-ink-meta">{t('jobs.cronHelp')}</p>
          {schedule.upcoming.length > 0 ? (
            <p data-job-upcoming className="text-meta text-ink-meta">
              {t('jobs.upcoming', { values: { times: schedule.upcoming.map((at) => formatDateTime(at, format)).join(' · ') } })}
            </p>
          ) : null}
          {runs.data ? (
            <ul data-job-runs className="flex flex-col gap-1">
              {runs.data.runs.map((run) => (
                <li key={run.runId} data-job-run={run.status} className="text-meta text-ink-600">
                  {formatDateTime(run.startedAt, format)} · {t(`jobs.status.${run.status}`)} · {t(`jobs.trigger.${run.trigger}`)}
                  {run.durationMs !== null ? ` · ${formatCount(run.durationMs, format)} ms` : ''}
                  {run.detail ? ` - ${run.detail}` : ''}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
